import React, { useEffect, useRef, useState } from 'react';
import { Modal } from '@toluna-ui-toolkit/modals';
import { useEvent } from '../../common/hooks/useEvent.hook';
import { betaSuggestionModalEvents } from '../../common/events/betaSuggestion.events';
import {
    BetaSuggestionModalBody,
    BetaSuggestionModalWrapper,
    CheckBoxLabel,
    CloseButton,
    ModalBody,
    MainTitle,
    Description,
    BetaButtonWrapper,
    BetaButtonStyles,
    CurrentButtonStyles,
    FeaturesWrapper,
    FeaturesTitle,
    FeaturesListWrapper,
    Feature,
    ClassicBuildButtonWrapper,
    CheckboxWrapper,
    ComingSoonWrapper,
    ComingSoonLabel,
    AnnounceIconWrapper,
    ComingSoonLabelTriangle,
    ComingSoonText,
} from './BetaSuggestion.style';
import { NON_SUPPORTED_FEATURES, TRANSLATIONS_PREFIX } from '../constants/constants';
import { CUSTOM_SURVEY_ID } from '../../walkme/walkme.module';
import { useFocusTrap } from '../../common/hooks/useTrapFocus';
import { AnnounceIcon, HeaderImage, SparkleIcon, TimesIcon } from '../../../assets/BetaSuggestion';
import { TButton, TCheckbox } from '@toluna/design-system';

interface IClickTemplateItemHandlerProps {
    linkTo: string;
    id: number;
}

interface IBetaSuggestionModalProps {
    translations: { [key: string]: string };
    clickSolutionItemHandler: (item: IClickTemplateItemHandlerProps) => void;
}

const BetaSuggestionModal = ({ translations, clickSolutionItemHandler }: IBetaSuggestionModalProps): JSX.Element => {
    const [showModal, setShowModal] = useState(false);
    const [dontShowAgain, setDontShowAgain] = useState(false);

    const handleShowModal = () => {
        setShowModal(true);
    };

    const handleHideModal = () => {
        setShowModal(false);
    };

    const handleModalShowNextTime = () => {
        localStorage.setItem('dontShowBetaModalAgain', String(dontShowAgain));
        handleHideModal();
    };

    useEffect(() => {
        if (showModal) document.documentElement.style.overflow = 'hidden';
        else document.documentElement.style.overflow = '';
    }, [showModal]);

    useEvent({ name: betaSuggestionModalEvents.SHOW_BETA_SUGGESTION, handler: () => handleShowModal() });
    useEvent({ name: betaSuggestionModalEvents.HIDE_BETA_SUGGESTION, handler: () => handleHideModal() });
    const betaSuggestionRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (showModal) document.getElementById('buildButtonNew')?.focus();

        const handleEscapeKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (showModal) {
                    e.stopPropagation();
                    handleHideModal();
                    window.tcp.createCustomSurvey?.createCustomSurveyButtonRef?.current?.focus();
                }
            }
        };
        window.addEventListener('keydown', handleEscapeKey, true);

        return () => {
            window.removeEventListener('keydown', handleEscapeKey, true);
        };
    }, [showModal]);

    const handleButtonAction = (linkTo: string, id: number) => {
        const item: IClickTemplateItemHandlerProps = { linkTo, id };
        handleModalShowNextTime();
        clickSolutionItemHandler(item);
    };
    const handleKeyDownButton = (event: React.KeyboardEvent, linkTo: string, id: number) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleButtonAction(linkTo, id);
        }
    };

    const handleKeyDownCheckbox = (
        e: React.KeyboardEvent,
        setDontShowAgain: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setDontShowAgain(prev => !prev);
        }
    };

    const handleKeyDownCloseButton = (e: React.KeyboardEvent, handleHideModal: () => void) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleHideModal();
            window.tcp.createCustomSurvey?.createCustomSurveyButtonRef?.current?.focus();
        }
    };

    useFocusTrap({
        containerRef: betaSuggestionRef,
        onEscape: () => handleModalShowNextTime(),
    });

    return showModal ? (
        <BetaSuggestionModalWrapper id="betaModal" tabIndex={0} ref={betaSuggestionRef}>
            <Modal showModal={showModal} closeModal={() => setShowModal(false)} customStyles={{ width: '694px' }}>
                <BetaSuggestionModalBody>
                    <CloseButton
                        tabIndex={0}
                        onClick={handleHideModal}
                        onKeyDown={e => handleKeyDownCloseButton(e, handleHideModal)}
                    >
                        <TimesIcon />
                    </CloseButton>
                    <HeaderImage />
                    <ModalBody>
                        <MainTitle>{translations[`${TRANSLATIONS_PREFIX}Main_Text`]}</MainTitle>
                        <Description>{translations[`${TRANSLATIONS_PREFIX}Description`]}</Description>
                        <BetaButtonWrapper>
                            <TButton
                                appearance="filled"
                                mainColor="secondary"
                                size="medium"
                                onClick={() => handleButtonAction('/define/?isNew=1', CUSTOM_SURVEY_ID)}
                                onKeyDown={(event: React.KeyboardEvent<Element>) =>
                                    handleKeyDownButton(event, '/define/?isNew=1', CUSTOM_SURVEY_ID)
                                }
                                id={'buildButtonNew'}
                                sx={BetaButtonStyles}
                            >
                                {translations[`${TRANSLATIONS_PREFIX}Button_New`]}
                            </TButton>
                        </BetaButtonWrapper>
                        <FeaturesWrapper>
                            <ComingSoonWrapper>
                                <ComingSoonLabel>
                                    <AnnounceIconWrapper>
                                        <AnnounceIcon />
                                    </AnnounceIconWrapper>
                                    <ComingSoonText>{translations[`${TRANSLATIONS_PREFIX}Coming_Soon`]}</ComingSoonText>
                                </ComingSoonLabel>
                                <ComingSoonLabelTriangle />
                            </ComingSoonWrapper>
                            <FeaturesTitle>
                                {translations[`${TRANSLATIONS_PREFIX}Non_Supported_Features_Note`]}
                            </FeaturesTitle>
                            <FeaturesListWrapper>
                                {NON_SUPPORTED_FEATURES.map(feature => (
                                    <Feature key={feature.translationKey}>
                                        <SparkleIcon />
                                        {translations[`${TRANSLATIONS_PREFIX}${feature.translationKey}`]}
                                    </Feature>
                                ))}
                            </FeaturesListWrapper>
                            <ClassicBuildButtonWrapper>
                                <TButton
                                    appearance="filled"
                                    mainColor="neutral"
                                    isElevated={true}
                                    onClick={() => handleButtonAction('/define', CUSTOM_SURVEY_ID)}
                                    onKeyDown={(event: React.KeyboardEvent<Element>) =>
                                        handleKeyDownButton(event, '/define', CUSTOM_SURVEY_ID)
                                    }
                                    id={'buildButtonCurrent'}
                                    sx={CurrentButtonStyles}
                                >
                                    {translations[`${TRANSLATIONS_PREFIX}Button_Current`]}
                                </TButton>
                            </ClassicBuildButtonWrapper>
                        </FeaturesWrapper>
                        <CheckboxWrapper
                            onClick={() => setDontShowAgain(!dontShowAgain)}
                            onKeyDown={e => handleKeyDownCheckbox(e, setDontShowAgain)}
                        >
                            <TCheckbox checked={dontShowAgain} />
                            <CheckBoxLabel tabIndex={-1}>
                                {translations[`${TRANSLATIONS_PREFIX}DoNotShowAgain`]}
                            </CheckBoxLabel>
                        </CheckboxWrapper>
                    </ModalBody>
                </BetaSuggestionModalBody>
            </Modal>
        </BetaSuggestionModalWrapper>
    ) : (
        <div />
    );
};
export default BetaSuggestionModal;
